import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchJSON, formatParams } from 'modules/helpers/fetch';
import isClient from 'helpers/isClient';
import { ProductListPartialViewModel } from 'autogen/swagger/all';
import useHasSearchParamsChanged from './useHasSearchParamsChanged';
import useUncommittedSearchParams from '../../components/ProductListFilter/shared/hooks/useUncommittedSearchParams';

const groupParamsByKey = (params) =>
    [...params.entries()].reduce((acc, tuple) => {
        // getting the key and value from each tuple
        const [key, val] = tuple;
        if (Object.prototype.hasOwnProperty.call(acc, key)) {
            // if the current key is already an array, we'll add the value to it
            if (Array.isArray(acc[key])) {
                acc[key] = [...acc[key], val];
            } else {
                // if it's not an array, but contains a value, we'll convert it into an array
                // and add the current value to it
                acc[key] = [acc[key], val];
            }
        } else {
            // plain assignment if no special case is present
            acc[key] = val;
        }

        return acc;
    }, {});

export default function useGetProductListPartialQuery(
    initialModel: ProductListPartialViewModel,
    queryParameters,
    enabled,
    baseQueryKey = 'productListPartial'
) {
    /*
        The reason we don't use a swagger endpoint is because of the way the routing works in the backend.
        each plp route has a different endpoint baseUrl + baseUrlKeys/filterUrlKeys + searchParams.
        we cannot reflect the urlKeys/filterKeys in swagger(yet).
        An endpoint would look something like this:
        matas.dk/hudpleje/kropspleje?brand=aco.
        where the urlkeys/filterUrlKeys would be the hudpleje/kropspleje part.
    */
    const productListPartialEndpoint = isClient
        ? window.location.href.split(/[?#]/)[0] + formatParams({ ...queryParameters })
        : '/';

    /*
        Every time queryParameters changes we fetch new PLP data.
        the queryParameters are treated as part of the queryKey
        this also simplifies the logic greatly

        note: we don't set the initialModel as initialData in useQuery, because it takes precedence over the keepPreviousData property.
        this would cause the initialData to be shown as placeholder data between each subsequent fetch and we don't want that behavior.
        we want the previousData to be shown as placeholder between each subsequent fetch.
        we achieve this by only enabling the useQuery hook once the searchParams has changed.
    */
    const query = useQuery({
        enabled, // only enable the query if the searchParams has changed, otherwise just use the initialModel from the server.
        queryKey: [baseQueryKey, queryParameters],
        queryFn: () => fetchJSON<ProductListPartialViewModel>(productListPartialEndpoint),
        keepPreviousData: true,
        refetchOnWindowFocus: false,
        refetchOnMount: false
    });

    if (!query.data) return { ...query, data: initialModel };

    return query;
}

export function useGetProductListPartial(initialModel: ProductListPartialViewModel) {
    const [searchParams] = useSearchParams();
    const queryParameters = groupParamsByKey(searchParams);
    const hasSearchParamsChanged = useHasSearchParamsChanged(searchParams);

    return useGetProductListPartialQuery(initialModel, queryParameters, hasSearchParamsChanged);
}

export function useGetProductListPartialForFilterMenu(initialModel: ProductListPartialViewModel) {
    const { uncommittedSearchParams } = useUncommittedSearchParams();
    const queryParameters = groupParamsByKey(uncommittedSearchParams);
    const hasSearchParamsChanged = useHasSearchParamsChanged(uncommittedSearchParams);
    queryParameters.pagesize = '1'; // set page size to reduce amount of data
    const baseQueryKey = 'filterMenuData';

    return useGetProductListPartialQuery(initialModel, queryParameters, hasSearchParamsChanged, baseQueryKey);
}
